import React from "react";
import { Box, Chip, Container, Paper, Stack, Typography } from "@mui/material";

export function AdminPageShell({
    icon,
    eyebrow = "Admin workspace",
    title,
    description,
    children,
    maxWidth = "lg",
}) {
    return (
        <Box
            sx={{
                width: "100%",
                py: { xs: 1, md: 2 },
                px: 0,
                backgroundColor: "transparent",
            }}
        >
            <Container maxWidth={maxWidth} disableGutters sx={{ px: { xs: 0, md: 0 } }}>
                <Stack spacing={{ xs: 2, md: 3 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: { xs: '20px', md: '24px' },
                            px: { xs: 2, md: 3.5 },
                            py: { xs: 2.25, md: 3.25 },
                            color: "common.white",
                            background:
                                "linear-gradient(135deg, #4a90e2 0%, #6a5af9 50%, #9013fe 100%)",
                            boxShadow: "0 24px 60px rgba(80, 81, 196, 0.28)",
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                inset: { xs: 'auto -60px -60px auto', md: 'auto -80px -80px auto' },
                                width: { xs: 160, md: 220 },
                                height: { xs: 160, md: 220 },
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.16)',
                                filter: 'blur(2px)',
                            },
                        }}
                    >
                        <Stack spacing={1.5} sx={{ position: "relative", zIndex: 1, minWidth: 0 }}>
                            <Chip
                                icon={icon}
                                label={eyebrow}
                                sx={{
                                    alignSelf: "flex-start",
                                    maxWidth: "100%",
                                    color: "common.white",
                                    bgcolor: "rgba(255,255,255,0.14)",
                                    border: "1px solid rgba(255,255,255,0.18)",
                                    '& .MuiChip-icon': { color: 'common.white' },
                                    '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere' },
                                }}
                            />
                            <Box>
                                <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "1.55rem", sm: "1.85rem", md: "2.35rem" }, lineHeight: 1.15, overflowWrap: "anywhere" }}>
                                    {title}
                                </Typography>
                                {description && (
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mt: 1.5,
                                            maxWidth: 720,
                                            color: "rgba(255,255,255,0.9)",
                                            fontSize: { xs: "0.92rem", md: "1.02rem" },
                                            lineHeight: 1.6,
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {description}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </Paper>

                    {children}
                </Stack>
            </Container>
        </Box>
    );
}

export function AdminSurface({ children, sx = {} }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
                borderRadius: { xs: '20px', md: '24px' },
                border: "1px solid rgba(106, 90, 249, 0.12)",
                background: "rgba(255,255,255,0.94)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 14px 36px rgba(76, 81, 191, 0.1)",
                overflow: "hidden",
                minWidth: 0,
                ...sx,
            }}
        >
            {children}
        </Paper>
    );
}

export function AdminStat({ label, value, helper }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 1.5, md: 2 },
                borderRadius: { xs: '20px', md: '24px' },
                border: "1px solid rgba(106, 90, 249, 0.12)",
                background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(245,247,255,0.96) 100%)",
                overflow: "hidden",
            }}
        >
            <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 1 }}>
                {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#2d2a5a", overflowWrap: "anywhere" }}>
                {value}
            </Typography>
            {helper && (
                <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary", overflowWrap: "anywhere" }}>
                    {helper}
                </Typography>
            )}
        </Paper>
    );
}
